import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { AppDispatch } from "../../../redux/store";
import paths from "../../../navigation/paths";
import {
  Status,
  getAllIncidentsState,
  allIncidentsStateAction,
  urgencyIcons,
  statusIcons,
} from "../../../redux/features/incident-slice";
import Table from "../../../components/table";
import TableBodyCell from "../../../components/table-body-cell";
import Loading from "../../../components/loading";
import EmptyDataOverlay from "../../../components/empty-data-overlay";
import Button from "../../../components/button";
import IncidentLineChart from "../charts/incident-line-chart";
import ToastComponent from "../../../components/toast";
import PermissionGate from "../../../core/layouts/permission-gate";
import { setBreadcrumbState } from "../../../redux/features/common-slice";
import { getUserState } from "../../../redux/features/auth-slice";
import { UserRole } from "../../../redux/features/user-slice";
export default function IncidentDashboardPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userState = useSelector(getUserState);
  const allIncidentsState = useSelector(getAllIncidentsState);

  useEffect(() => {
    if (!userState) {
      return;
    }
    dispatch(allIncidentsStateAction());
    dispatch(
      setBreadcrumbState([
        { id: paths.INCIDENTS, path: null, title: "Incidents" },
        { id: "incidentsDashboard", path: null, title: "Dashboard" },
      ])
    );
  }, [dispatch, userState]);

  const pendingIncidents = allIncidentsState.data.filter(
    (incident) =>
      incident.status === Status.Pending ||
      incident.status === Status.Unresolved
  );

  return (
    <PermissionGate categories={[]} roles={[]}>
      <Helmet title="Dashboard" />
      <div className="h-full overflow-y-scroll space-y-6 flex flex-col">
        <h1 className="text-5xl font-bold text-info-dark text-center">
          Incident Dashboard
        </h1>
        <div className="p-4 bg-gray-100 rounded-lg text-sm">
          <ul className="list-disc list-inside">
            <li>
              <strong>Unresolved Incidents:</strong> Logged but not yet assigned
              an action plan.
            </li>
            <li>
              <strong>Pending Incidents:</strong> Action plan assigned and in
              progress.
            </li>
            <li>
              <strong>Resolved Incidents:</strong> Action plan completed, with
              no remaining customer concerns.
            </li>
          </ul>
        </div>
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="flex flex-col space-y-6">
            <div className="flex space-x-4">
              <Button
                label="List All Incidents"
                onClick={() => navigate(paths.INCIDENTS)}
                variant="filled"
              />
              <Button
                label="Log New Incident"
                onClick={() => navigate(paths.ADD_INCIDENT)}
                variant="outlined"
                disabled={
                  userState.data?.role !== UserRole.Admin &&
                  userState.data?.role !== UserRole.CustomerSupport
                }
              />
            </div>
            <div className="bg-white rounded-lg">
              <h2 className="text-lg font-semibold mb-2">
                Pending and Unresolved Incidents
              </h2>
              <div>
                <Table
                  headerCells={[
                    {
                      id: "title",
                      label: "Title",
                      align: "text-left",
                      width: "w-3/12",
                    },
                    {
                      id: "urgency",
                      label: "Urgency",
                      align: "text-left",
                      width: "w-2/12",
                    },
                    {
                      id: "technical-unit",
                      label: "Technical Unit",
                      align: "text-left",
                      width: "w-3/12",
                    },
                    {
                      id: "customer",
                      label: "Customer",
                      align: "text-left",
                      width: "w-2/12",
                    },
                    {
                      id: "status",
                      label: "Status",
                      align: "text-left",
                      width: "w-2/12",
                    },
                  ]}
                  body={
                    allIncidentsState.loading
                      ? [
                          <tr key="loading-row">
                            <td colSpan={5} className="h-32">
                              <div className="flex flex-col items-center justify-center">
                                <Loading />
                              </div>
                            </td>
                          </tr>,
                        ]
                      : pendingIncidents.length > 0
                      ? pendingIncidents.map((incident) => (
                          <tr
                            key={incident.id}
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() =>
                              navigate(`${paths.INCIDENTS}/${incident.id}`)
                            }
                          >
                            <TableBodyCell>{incident.title}</TableBodyCell>
                            <TableBodyCell>
                              {urgencyIcons[incident.urgencyLevel]}
                              {incident.urgencyLevel}
                            </TableBodyCell>
                            <TableBodyCell>
                              <label className="whitespace-normal break-words">
                                {incident.technicalUnits
                                  ?.map((unit) => unit.name)
                                  .join(", ") ?? ""}
                              </label>
                            </TableBodyCell>
                            <TableBodyCell>
                              {incident.customer.name}
                            </TableBodyCell>
                            <TableBodyCell>
                              {statusIcons[incident.status]}
                              {incident.status}
                            </TableBodyCell>
                          </tr>
                        ))
                      : [
                          <tr key="empty-row">
                            <td colSpan={5} className="h-32">
                              <div className="flex flex-col items-center justify-center">
                                <EmptyDataOverlay
                                  label="No pending incidents"
                                  exClassName="h-20"
                                />
                              </div>
                            </td>
                          </tr>,
                        ]
                  }
                />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg">
            <h2 className="text-lg font-semibold mb-2">Incidents Overview</h2>
            <IncidentLineChart />
          </div>
        </div>
      </div>
      <ToastComponent id="incidentsDashboard" />
    </PermissionGate>
  );
}
